
  import React from 'react';
  import { MaintenanceItem, Status, Priority } from '../../types';
  import StatusBadge from '../../components/StatusBadge';
  import PriorityIcon from '../../components/PriorityIcon';
  import { getPriorityFromApi } from './priorityHelper';
  import { formatDateAgo, isOverdue } from '../../utils/dateHelper';

  /**
   * Props for the MaintenanceCard component.
   */
  interface MaintenanceCardProps {
    item: MaintenanceItem;
    onViewDetails: (itemId: number) => void;
    onMarkAsCompleted: (itemId: number) => void;
    onDragStart?: (e: React.DragEvent<HTMLDivElement>) => void;
    isDragging?: boolean;
  }

  /**
   * A component that displays a single maintenance item in a card format.
   * It is used in both the Grid and Kanban views and supports being dragged.
   * @param {MaintenanceCardProps} props - The props for the component.
   * @returns {React.ReactElement} - The rendered maintenance card.
   */
  const MaintenanceCard: React.FC<MaintenanceCardProps> = ({ item, onViewDetails, onMarkAsCompleted, onDragStart, isDragging }) => {
    const priority = getPriorityFromApi(item.priority);
    const formattedDate = new Date(item.created_at).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
    const daysAgo = formatDateAgo(item.created_at);
    const isCompleted = item.status === Status.Completed;
    const isCritical = priority === Priority.Critical;
    const isTaskOverdue = isOverdue(item.created_at, item.status);
    const isOptimistic = item.isOptimistic;
    const canDrag = !isCompleted && !isOptimistic;

    const handleCompleteClick = (e: React.MouseEvent) => {
      e.stopPropagation(); 
      onMarkAsCompleted(item.id);
    };
    
    // Helper to strip HTML tags for plain text display in the card preview
    const getPlainDescription = (html: string) => {
      if (!html) return '';
      // Replace paragraph endings and breaks with spaces to avoid words running together
      const withSpaces = html
        .replace(/<\/p>/gi, ' ')
        .replace(/<br\s*\/?>/gi, ' ');
      // Strip all tags
      const stripped = withSpaces.replace(/<[^>]+>/g, '');
      // Decode common entities if necessary (basic ones) or leave as is if simple text. 
      // For a robust solution, DOMParser is better but regex is faster for previews.
      // Let's use a temporary DOM element to decode entities correctly as well.
      const tmp = document.createElement("DIV");
      tmp.innerHTML = stripped;
      return (tmp.textContent || tmp.innerText || "").trim();
    };

    const plainDescription = getPlainDescription(item.description);

    const cardClasses = `
      bg-white rounded-xl shadow-lg hover:shadow-xl transition-all 
      duration-300 ease-in-out overflow-hidden flex flex-col
      ${isCompleted ? 'bg-gray-50 opacity-80' : ''}
      ${isOptimistic ? 'opacity-60 animate-pulse' : ''}
      ${!isCompleted && isCritical ? 'border-l-4 border-red-500' : ''}
      ${!isCompleted && !isCritical && isTaskOverdue ? 'border-l-4 border-yellow-500' : ''}
      ${isDragging ? 'opacity-40 ring-2 ring-blue-500 ring-offset-2 transform rotate-2' : ''}
      ${canDrag ? 'cursor-grab active:cursor-grabbing' : ''}
    `;

    return (
      <div
        className={cardClasses}
        draggable={canDrag}
        onDragStart={onDragStart}
      >
        <button 
          onClick={isOptimistic ? undefined : () => onViewDetails(item.id)}
          disabled={isOptimistic}
          className="p-5 flex-grow text-left w-full focus:outline-none disabled:cursor-wait"
          aria-label={`View details for ${item.title}`}
        >
          <div className="flex justify-between items-start mb-3">
            <h3 className="font-bold text-lg text-gray-800 mr-4 leading-tight">
              <span className="text-gray-500 mr-2">#{item.id}</span>
              {item.title}
            </h3>
            <PriorityIcon priority={priority} />
          </div>
          <p className="text-gray-600 text-sm mb-4 h-10 overflow-hidden line-clamp-2">{plainDescription}</p>
          
          <div className="space-y-3 text-sm text-gray-500">
              <div className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span>Reported on: {formattedDate} <span className="text-gray-400">{daysAgo}</span></span>
              </div>
              <div className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <span>Assigned to:
                      <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                          {item.assigned_to_full_name || 'Unassigned'}
                      </span>
                  </span>
              </div>
          </div>
        </button>
        <div className="bg-gray-50 px-5 py-3 border-t border-gray-200 flex justify-between items-center">
          <StatusBadge status={item.status as Status} />
          <div className="flex items-center gap-3">
              {!isCompleted && !isOptimistic && (
                <button
                  onClick={handleCompleteClick}
                  className="inline-flex items-center justify-center p-2 border border-transparent text-xs font-medium rounded-full shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-all transform hover:scale-105"
                  title="Mark as complete"
                  aria-label={`Mark "${item.title}" as complete`}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
              )}
              <div className="flex items-center text-sm text-gray-500 gap-1" title={`${item.images?.length || 0} attachments`}>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M8 4a3 3 0 00-3 3v4a3 3 0 006 0V7a1 1 0 112 0v4a5 5 0 01-10 0V7a5 5 0 0110 0v4a1 1 0 11-2 0V7a3 3 0 00-3-3z" clipRule="evenodd" />
                  </svg>
                  <span>{item.images?.length || 0}</span>
              </div>
              <div className="flex items-center text-sm text-gray-500 gap-1" title={`${item.messages?.length || 0} messages`}>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  <span>{item.messages?.length || 0}</span>
              </div>
          </div>
        </div>
      </div>
    );
  };

  export default MaintenanceCard;
